import * as React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Alert, Button, CustomInput, FormGroup } from 'reactstrap';
import { State } from '../../redux/state';
import { LProps, withLocalization } from '../../utils/wrappers';
import { userApiBasePath } from '../../redux/selectors/userApi';
import * as selectors from '../../redux/selectors';

interface NotificationPreference {
  emailComments: boolean;
  emailDivisionAnnouncements: boolean;
  emailCompetitionAnnouncements: boolean;
  emailReminders: boolean;
}

interface Props {
  apiBase: string;
  token: string;
  competitionName: string;
}

interface LocalState {
  prefs: NotificationPreference | null;
  loading: boolean;
  saving: boolean;
  error: string;
  notice: string;
}

const defaultPrefs: NotificationPreference = {
  emailComments: false,
  emailDivisionAnnouncements: true,
  emailCompetitionAnnouncements: true,
  emailReminders: true
};

class NotificationPreferences extends React.Component<LProps<Props>, LocalState> {
  public state: LocalState = {
    prefs: null,
    loading: true,
    saving: false,
    error: '',
    notice: ''
  };

  public componentDidMount() {
    this.loadPreferences();
  }

  private authHeaders = () => ({
    'Authorization': this.props.token,
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  })

  private loadPreferences = async () => {
    const { apiBase, competitionName, token } = this.props;
    if (!token || !competitionName) {
      this.setState({ loading: false, error: 'Sign in to manage notification preferences.' });
      return;
    }
    this.setState({ loading: true, error: '' });
    try {
      const response = await fetch(
        `${apiBase}/competitions/${encodeURIComponent(competitionName)}/notification_preferences`,
        { headers: this.authHeaders() }
      );
      if (!response.ok) {
        this.setState({ loading: false, error: `Failed to load preferences (${response.status}).` });
        return;
      }
      const payload = await response.json();
      const prefs = payload.notificationPreference || defaultPrefs;
      this.setState({ prefs, loading: false });
    } catch (_err) {
      this.setState({ loading: false, error: 'Network error loading preferences.' });
    }
  }

  private togglePref = (field: keyof NotificationPreference) => {
    const { prefs } = this.state;
    if (!prefs) return;
    this.setState({
      prefs: { ...prefs, [field]: !prefs[field] },
      notice: ''
    });
  }

  private savePreferences = async () => {
    const { apiBase, competitionName } = this.props;
    const { prefs } = this.state;
    if (!prefs) return;
    this.setState({ saving: true, error: '', notice: '' });
    try {
      const response = await fetch(
        `${apiBase}/competitions/${encodeURIComponent(competitionName)}/notification_preferences`,
        {
          method: 'PUT',
          headers: this.authHeaders(),
          body: JSON.stringify(prefs)
        }
      );
      if (!response.ok) {
        this.setState({ saving: false, error: `Failed to save preferences (${response.status}).` });
        return;
      }
      const payload = await response.json();
      this.setState({
        prefs: payload.notificationPreference || prefs,
        saving: false,
        notice: 'Notification preferences saved.'
      });
    } catch (_err) {
      this.setState({ saving: false, error: 'Network error saving preferences.' });
    }
  }

  public render() {
    const { t } = this.props;
    const { prefs, loading, saving, error, notice } = this.state;

    return (
      <div className="cc__white_box">
        <h3>{t('Notification preferences')}</h3>
        <p className="cc__lighttext">
          {t('In-app notifications always appear in your notification bell. These settings control optional email copies.')}
        </p>
        {error && <Alert color="danger" role="alert">{error}</Alert>}
        {notice && <Alert color="success" role="status">{notice}</Alert>}
        {loading && <p>{t('Loading…')}</p>}

        {!loading && prefs && (
          <div>
            <FormGroup>
              <CustomInput
                type="checkbox"
                id="pref-email-comments"
                label={t('Email me when someone comments on my story')}
                checked={prefs.emailComments}
                onChange={() => this.togglePref('emailComments')}
              />
            </FormGroup>
            <FormGroup>
              <CustomInput
                type="checkbox"
                id="pref-email-division"
                label={t('Email me division announcements from my Division Captain')}
                checked={prefs.emailDivisionAnnouncements}
                onChange={() => this.togglePref('emailDivisionAnnouncements')}
              />
            </FormGroup>
            <FormGroup>
              <CustomInput
                type="checkbox"
                id="pref-email-competition"
                label={t('Email me competition-wide announcements')}
                checked={prefs.emailCompetitionAnnouncements}
                onChange={() => this.togglePref('emailCompetitionAnnouncements')}
              />
            </FormGroup>
            <FormGroup>
              <CustomInput
                type="checkbox"
                id="pref-email-reminders"
                label={t('Email me launch reminders and Challenge updates')}
                checked={prefs.emailReminders}
                onChange={() => this.togglePref('emailReminders')}
              />
            </FormGroup>
            <Button color="primary" onClick={this.savePreferences} disabled={saving}>
              {saving ? t('Saving…') : t('Save preferences')}
            </Button>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: State) => ({
  apiBase: userApiBasePath(state),
  token: selectors.token(state),
  competitionName: selectors.competitionName(state)
});

export default compose(
  connect(mapStateToProps),
  withLocalization('account')
)(NotificationPreferences);
